import { Observable } from '@zephyr3d/base';
import { CPUTimer } from './timer.js';
import { getVertexAttribFormat, makeVertexBufferType, getVertexFormatSize, GPUResourceUsageFlags } from './gpuobject.js';
import { Pool } from './pool.js';
import { ProgramBuilder } from './builder/programbuilder.js';
import { DrawText } from './helpers/drawtext.js';

class ResizeHandler {
    _canvas;
    _dpr;
    _cssWidth;
    _cssHeight;
    _deviceWidth;
    _deviceHeight;
    _resizeObserver;
    _mutationObserver;
    _onResizeCallback;
    constructor(canvas, onResize, dpr){
        this._canvas = canvas;
        this._dpr = dpr;
        this._cssWidth = 0;
        this._cssHeight = 0;
        this._deviceWidth = 0;
        this._deviceHeight = 0;
        this._onResizeCallback = onResize;
    }
    init() {
        const canvas = this._canvas;
        if (window.ResizeObserver) {
            this._resizeObserver = new ResizeObserver((entries)=>{
                const entry = entries[0];
                this._handleResizeEntry(entry);
            });
            try {
                this._resizeObserver.observe(canvas);
            } catch  {
                this._resizeObserver.observe(canvas);
            }
        } else {
            if (window.MutationObserver) {
                this._mutationObserver = new MutationObserver((mutations)=>{
                    if (mutations.length > 0) {
                        this._handleLegacyResize();
                    }
                });
                this._mutationObserver.observe(canvas, {
                    attributes: true,
                    attributeFilter: [
                        'style'
                    ]
                });
            }
            window.addEventListener('resize', this._handleLegacyResize);
            this._handleLegacyResize();
        }
    }
    _handleResizeEntry(_entry) {
        // CSS pixel
        const boxSize = Array.isArray(_entry.contentBoxSize) ? _entry.contentBoxSize[0] : _entry.contentBoxSize;
        const cssWidth = boxSize?.inlineSize ?? this._canvas.clientWidth;
        const cssHeight = boxSize?.blockSize ?? this._canvas.clientHeight;
        // Device pixel
        /*
    let deviceWidth: number;
    let deviceHeight: number;

    const dpBox = (entry as any).devicePixelContentBoxSize?.[0];
    if (dpBox) {
      deviceWidth = dpBox.inlineSize;
      deviceHeight = dpBox.blockSize;
    } else {
      deviceWidth = Math.round(cssWidth * this._dpr);
      deviceHeight = Math.round(cssHeight * this._dpr);
    }
    */ const deviceWidth = cssWidth * this._dpr;
        const deviceHeight = cssHeight * this._dpr;
        if (cssWidth === this._cssWidth && cssHeight === this._cssHeight && deviceWidth === this._deviceWidth && deviceHeight === this._deviceHeight) {
            return;
        }
        this._cssWidth = cssWidth;
        this._cssHeight = cssHeight;
        this._deviceWidth = deviceWidth;
        this._deviceHeight = deviceHeight;
        this._onResizeCallback(cssWidth, cssHeight, deviceWidth, deviceHeight);
    }
    _handleLegacyResize = ()=>{
        const cssWidth = this._canvas.clientWidth;
        const cssHeight = this._canvas.clientHeight;
        const dpr = window.devicePixelRatio || 1;
        const deviceWidth = Math.round(cssWidth * dpr);
        const deviceHeight = Math.round(cssHeight * dpr);
        if (cssWidth === this._cssWidth && cssHeight === this._cssHeight && deviceWidth === this._deviceWidth && deviceHeight === this._deviceHeight) {
            return;
        }
        this._cssWidth = cssWidth;
        this._cssHeight = cssHeight;
        this._deviceWidth = deviceWidth;
        this._deviceHeight = deviceHeight;
        this._onResizeCallback(cssWidth, cssHeight, deviceWidth, deviceHeight);
    };
    dispose() {
        if (this._resizeObserver) {
            this._resizeObserver.disconnect();
        }
        if (this._mutationObserver) {
            this._mutationObserver.disconnect();
        }
        window.removeEventListener('resize', this._handleLegacyResize);
    }
}
/**
 * Base class for rendering device
 * @public
 */ class BaseDevice extends Observable {
    _dpr;
    _canvas;
    _canvasClientWidth;
    _canvasClientHeight;
    _gpuObjectList;
    _gpuMemCost;
    _disposeObjectList;
    _beginFrameTime;
    _endFrameTime;
    _frameInfo;
    _cpuTimer;
    _gpuTimer;
    _runningLoop;
    _fpsCounter;
    _runLoopFunc;
    _backend;
    _beginFrameCounter;
    _programBuilder;
    _poolMap;
    _defaultPoolKey;
    _temporalFramebuffer;
    _vSync;
    _resizer;
    _stateStack;
    constructor(cvs, backend, dpr){
        super();
        this._dpr = dpr ?? window.devicePixelRatio ?? 1;
        this._backend = backend;
        this._gpuObjectList = {
            stacks: new Map(),
            textures: [],
            samplers: [],
            buffers: [],
            programs: [],
            framebuffers: [],
            vertexArrayObjects: [],
            bindGroups: []
        };
        this._canvas = cvs;
        this._canvas.setAttribute('tabindex', '1');
        this._canvasClientWidth = cvs.clientWidth;
        this._canvasClientHeight = cvs.clientHeight;
        this._gpuMemCost = 0;
        this._disposeObjectList = [];
        this._beginFrameTime = 0;
        this._endFrameTime = 0;
        this._runLoopFunc = null;
        this._frameInfo = {
            frameCounter: 0,
            frameTimestamp: 0,
            elapsedTimeCPU: 0,
            elapsedTimeGPU: 0,
            elapsedFrame: 0,
            elapsedOverall: 0,
            FPS: 0,
            drawCalls: 0,
            computeCalls: 0,
            nextFrameCall: [],
            nextFrameCallNext: []
        };
        this._programBuilder = new ProgramBuilder(this);
        this._cpuTimer = new CPUTimer();
        this._gpuTimer = null;
        this._runningLoop = null;
        this._fpsCounter = {
            time: 0,
            frame: 0
        };
        this._stateStack = [];
        this._beginFrameCounter = 0;
        this._poolMap = new Map();
        this._defaultPoolKey = Symbol('defaultPool');
        this._poolMap.set(this._defaultPoolKey, new Pool(this, this._defaultPoolKey));
        this._temporalFramebuffer = false;
        this._temporalFramebuffer = false;
        this._vSync = true;
        //this._registerEventHandlers();
        this._resizer = new ResizeHandler(this._canvas, (cssWidth, cssHeight, deviceWidth, deviceHeight)=>{
            this.dispatchEvent('resize', cssWidth, cssHeight, deviceWidth, deviceHeight);
        }, this._dpr);
    }
    getScaleX() {
        return this._canvas.clientWidth === 0 ? 0 : this._canvas.width / this._canvas.clientWidth;
    }
    getScaleY() {
        return this._canvas.clientHeight === 0 ? 0 : this._canvas.height / this._canvas.clientHeight;
    }
    get backend() {
        return this._backend;
    }
    get videoMemoryUsage() {
        return this._gpuMemCost;
    }
    get frameInfo() {
        return this._frameInfo;
    }
    get isRendering() {
        return this._runningLoop !== null;
    }
    get canvas() {
        return this._canvas;
    }
    get type() {
        return this._backend.typeName();
    }
    get vSync() {
        return this._vSync;
    }
    set vSync(val) {
        this._vSync = !!val;
    }
    get pool() {
        return this._poolMap.get(this._defaultPoolKey);
    }
    get runLoopFunction() {
        return this._runLoopFunc;
    }
    get programBuilder() {
        return this._programBuilder;
    }
    poolExists(key) {
        return this._poolMap.has(key);
    }
    getPool(key) {
        let pool = this._poolMap.get(key);
        if (!pool) {
            pool = new Pool(this, key);
            this._poolMap.set(key, pool);
        }
        return pool;
    }
    setFont(fontName) {
        DrawText.setFont(this, fontName);
    }
    setTextRenderStates(renderStates) {
        DrawText.setRenderStates(renderStates);
    }
    drawText(text, xOrRect, yOrColor, colorOrOptions, options) {
        if (typeof xOrRect === 'number') {
            DrawText.drawText(this, text, colorOrOptions, xOrRect, yOrColor);
        } else {
            DrawText.drawText(this, text, yOrColor, xOrRect, options ?? colorOrOptions);
        }
    }
    setFramebuffer(colorOrRT, depth, sampleCount) {
        let newRT = null;
        let temporal = false;
        if (!Array.isArray(colorOrRT)) {
            newRT = colorOrRT ?? null;
        } else {
            newRT = this.pool.fetchTemporalFramebuffer(false, 0, 0, colorOrRT, depth, true, sampleCount);
            temporal = true;
        }
        const currentRT = this.getFramebuffer();
        if (currentRT !== newRT) {
            if (this._temporalFramebuffer && currentRT) {
                this.pool.releaseFrameBuffer(currentRT);
            }
            this._temporalFramebuffer = temporal;
            this._setFramebuffer(newRT);
        }
    }
    disposeObject(obj, remove = true) {
        if (obj) {
            if (remove) {
                this.removeGPUObject(obj);
            }
            if (this.isContextLost()) {
                obj.destroy();
            } else {
                this._disposeObjectList.push(obj);
            }
        }
    }
    restoreObject(obj) {
        if (obj && obj.disposed && !this.isContextLost()) {
            obj.restore();
            obj.restoreHandler?.(obj);
        }
    }
    enableGPUTimeRecording(enable) {
        if (enable && !this._gpuTimer) {
            this._gpuTimer = this.createGPUTimer();
        } else if (!enable) {
            this._gpuTimer?.end();
            this._gpuTimer = null;
        }
    }
    beginFrame() {
        if (this._beginFrameCounter === 0) {
            for (const obj of this._disposeObjectList){
                obj.destroy();
            }
            this._disposeObjectList = [];
        }
        this._beginFrameCounter++;
        this._beginFrameTime = this._cpuTimer.now();
        this.updateFrameInfo();
        this._poolMap.forEach((pool)=>pool.autoRelease());
        return this.onBeginFrame();
    }
    endFrame() {
        if (this._beginFrameCounter > 0) {
            this._beginFrameCounter--;
            if (this._beginFrameCounter === 0) {
                this._endFrameTime = this._cpuTimer.now();
                this._frameInfo.frameCounter++;
                this.onEndFrame();
            }
        }
    }
    getVertexAttribFormat(semantic, dataType, componentCount) {
        return getVertexAttribFormat(semantic, dataType, componentCount);
    }
    createInterleavedVertexBuffer(attribFormats, data, options) {
        if (options && options.usage && options.usage !== 'vertex') {
            console.error(`createInterleavedVertexBuffer() failed: options.usage must be 'vertex' or not set`);
            return null;
        }
        let size = 0;
        for (const format of attribFormats){
            size += getVertexFormatSize(format);
        }
        const vertexBufferType = makeVertexBufferType(data.byteLength / size >> 0, ...attribFormats);
        if (!vertexBufferType) {
            console.error('createInterleavedVertexBuffer() failed: Cannot determine vertex buffer type');
            return null;
        }
        const opt = Object.assign({
            usage: 'vertex',
            dynamic: false,
            managed: true,
            storage: false
        }, options || {});
        if (opt.storage) {
            opt.dynamic = false;
            opt.managed = false;
        }
        if (opt.dynamic) {
            opt.managed = false;
        }
        return this.createStructuredBuffer(vertexBufferType, opt, data);
    }
    createVertexBuffer(attribFormat, data, options) {
        if (options && options.usage && options.usage !== 'vertex') {
            console.error(`createVertexBuffer() failed: options.usage must be 'vertex' or not set`);
            return null;
        }
        const count = getVertexFormatSize(attribFormat);
        const vertexBufferType = makeVertexBufferType(data.byteLength / count >> 0, attribFormat);
        if (!vertexBufferType) {
            console.error('createInterleavedVertexBuffer() failed: Cannot determine vertex buffer type');
            return null;
        }
        const opt = Object.assign({
            usage: 'vertex',
            dynamic: false,
            managed: true,
            storage: false
        }, options || {});
        if (opt.storage) {
            opt.dynamic = false;
            opt.managed = false;
        }
        if (opt.dynamic) {
            opt.managed = false;
        }
        return this.createStructuredBuffer(vertexBufferType, opt, data);
    }
    draw(primitiveType, first, count) {
        this._frameInfo.drawCalls++;
        this._draw(primitiveType, first, count);
    }
    drawInstanced(primitiveType, first, count, numInstances) {
        this._frameInfo.drawCalls++;
        this._drawInstanced(primitiveType, first, count, numInstances);
    }
    executeRenderBundle(renderBundle) {
        this._frameInfo.drawCalls += this._executeRenderBundle(renderBundle);
    }
    compute(workgroupCountX, workgroupCountY, workgroupCountZ) {
        this._frameInfo.computeCalls++;
        this._compute(workgroupCountX, workgroupCountY, workgroupCountZ);
    }
    runNextFrame(f) {
        if (f) {
            this._frameInfo.nextFrameCall.push(f);
        }
    }
    async runNextFrameAsync(f) {
        return new Promise((resolve)=>{
            if (f) {
                this._frameInfo.nextFrameCall.push(()=>{
                    const p = f();
                    if (p instanceof Promise) {
                        p.then(()=>resolve());
                    } else {
                        resolve();
                    }
                });
            } else {
                resolve();
            }
        });
    }
    exitLoop() {
        if (this._runningLoop !== null) {
            if (this._runningLoop !== 0) {
                cancelAnimationFrame(this._runningLoop);
            } else {
                this.cancelNextFrame(this._runningLoop);
            }
            this._runningLoop = null;
        }
    }
    runLoop(func) {
        if (this._runningLoop !== null) {
            console.error('Device.runLoop() can not be nested');
            return;
        }
        if (!func) {
            console.error('Device.runLoop() argment error');
            return;
        }
        const that = this;
        that._runLoopFunc = func;
        (function entry() {
            if (that._vSync) {
                that._runningLoop = requestAnimationFrame(entry);
            } else {
                that._runningLoop = that.nextFrame(()=>{
                    if (that._runningLoop !== null) {
                        entry();
                    }
                });
            }
            if (that.beginFrame()) {
                that._runLoopFunc(that);
                that.endFrame();
            }
        })();
    }
    pushDeviceStates() {
        this._stateStack.push({
            windowOrderReversed: this.isWindingOrderReversed(),
            framebuffer: this.getFramebuffer(),
            viewport: this.getViewport(),
            scissor: this.getScissor(),
            program: this.getProgram(),
            renderStateSet: this.getRenderStates(),
            vertexLayout: this.getVertexLayout(),
            bindGroups: [
                this.getBindGroup(0),
                this.getBindGroup(1),
                this.getBindGroup(2),
                this.getBindGroup(3)
            ]
        });
    }
    popDeviceStates() {
        if (this._stateStack.length === 0) {
            console.error('Device.popDeviceStates(): stack is empty');
        } else {
            const top = this._stateStack.pop();
            this.setFramebuffer(top.framebuffer);
            this.setViewport(top.viewport);
            this.setScissor(top.scissor);
            this.setProgram(top.program);
            this.setRenderStates(top.renderStateSet);
            this.setVertexLayout(top.vertexLayout);
            this.setBindGroup(0, ...top.bindGroups[0]);
            this.setBindGroup(1, ...top.bindGroups[1]);
            this.setBindGroup(2, ...top.bindGroups[2]);
            this.setBindGroup(3, ...top.bindGroups[3]);
            this.reverseVertexWindingOrder(top.windowOrderReversed);
        }
    }
    getGPUObjects() {
        return this._gpuObjectList;
    }
    getGPUObjectById(uid) {
        for (const list of [
            this._gpuObjectList.textures,
            this._gpuObjectList.samplers,
            this._gpuObjectList.buffers,
            this._gpuObjectList.framebuffers,
            this._gpuObjectList.programs,
            this._gpuObjectList.vertexArrayObjects
        ]){
            for (const obj of list){
                if (obj.uid === uid) {
                    return obj;
                }
            }
        }
        return null;
    }
    screenXToDevice(val) {
        return this.getFramebuffer() ? val : Math.round(val * this.getScaleX());
    }
    deviceXToScreen(val) {
        return this.getFramebuffer() ? val : Math.round(val / this.getScaleX());
    }
    screenYToDevice(val) {
        return this.getFramebuffer() ? val : Math.round(val * this.getScaleY());
    }
    deviceYToScreen(val) {
        return this.getFramebuffer() ? val : Math.round(val / this.getScaleY());
    }
    buildRenderProgram(options) {
        return this._programBuilder.buildRenderProgram(options);
    }
    buildComputeProgram(options) {
        return this._programBuilder.buildComputeProgram(options);
    }
    getGPUObjectStack(obj) {
        return this._gpuObjectList.stacks.get(obj) ?? '';
    }
    addGPUObject(obj) {
        const list = this.getGPUObjectList(obj);
        if (list && list.indexOf(obj) < 0) {
            list.push(obj);
            this._gpuObjectList.stacks.set(obj, new Error().stack ?? '');
            this.dispatchEvent('gpuobject_added', obj);
        }
    }
    removeGPUObject(obj) {
        const list = this.getGPUObjectList(obj);
        if (list) {
            const index = list.indexOf(obj);
            if (index >= 0) {
                list.splice(index, 1);
                this._gpuObjectList.stacks.delete(obj);
                this.dispatchEvent('gpuobject_removed', obj);
            }
        }
    }
    updateVideoMemoryCost(delta) {
        this._gpuMemCost += delta;
    }
    async initResizer() {
        return new Promise((resolve)=>{
            this.once('resize', (cssWidth, cssHeight, deviceWidth, deviceHeight)=>{
                this._handleResize(cssWidth, cssHeight, deviceWidth, deviceHeight);
                this.on('resize', (cssWidth, cssHeight, deviceWidth, deviceHeight)=>{
                    this._handleResize(cssWidth, cssHeight, deviceWidth, deviceHeight);
                });
                resolve();
            });
            this._resizer.init();
        });
    }
    updateFrameInfo() {
        this._frameInfo.drawCalls = 0;
        this._frameInfo.computeCalls = 0;
        const now = this._beginFrameTime;
        if (this._frameInfo.frameTimestamp === 0) {
            this._frameInfo.frameTimestamp = now;
            this._frameInfo.elapsedTimeCPU = 0;
            this._frameInfo.elapsedTimeGPU = 0;
            this._frameInfo.elapsedFrame = 0;
            this._frameInfo.elapsedOverall = 0;
            this._frameInfo.FPS = 0;
            this._fpsCounter.time = now;
            this._fpsCounter.frame = this._frameInfo.frameCounter;
            if (this._gpuTimer) {
                this._gpuTimer.begin();
            }
        } else {
            this._frameInfo.elapsedFrame = now - this._frameInfo.frameTimestamp;
            this._frameInfo.elapsedOverall += this._frameInfo.elapsedFrame;
            let gpuTime = 0;
            let cpuTime = 0;
            if (this._endFrameTime !== 0) {
                gpuTime = now - this._endFrameTime;
                cpuTime = this._endFrameTime - this._frameInfo.frameTimestamp;
            }
            this._frameInfo.frameTimestamp = now;
            if (now >= this._fpsCounter.time + 1000) {
                this._frameInfo.FPS = (this._frameInfo.frameCounter - this._fpsCounter.frame) * 1000 / (now - this._fpsCounter.time);
                this._fpsCounter.time = now;
                this._fpsCounter.frame = this._frameInfo.frameCounter;
                this._frameInfo.elapsedTimeGPU = gpuTime;
                this._frameInfo.elapsedTimeCPU = cpuTime;
            }
        }
        const tmp = this._frameInfo.nextFrameCall;
        this._frameInfo.nextFrameCall = this._frameInfo.nextFrameCallNext;
        this._frameInfo.nextFrameCallNext = tmp;
        for (const f of this._frameInfo.nextFrameCallNext){
            f();
        }
        this._frameInfo.nextFrameCallNext.length = 0;
    }
    getGPUObjectList(obj) {
        let list = null;
        if (obj.isTexture()) {
            list = this._gpuObjectList.textures;
        } else if (obj.isSampler()) {
            list = this._gpuObjectList.samplers;
        } else if (obj.isBuffer()) {
            list = this._gpuObjectList.buffers;
        } else if (obj.isFramebuffer()) {
            list = this._gpuObjectList.framebuffers;
        } else if (obj.isProgram()) {
            list = this._gpuObjectList.programs;
        } else if (obj.isVertexLayout()) {
            list = this._gpuObjectList.vertexArrayObjects;
        } else if (obj.isBindGroup()) {
            list = this._gpuObjectList.bindGroups;
        }
        return list;
    }
    invalidateAll() {
        for (const list of [
            this._gpuObjectList.buffers,
            this._gpuObjectList.textures,
            this._gpuObjectList.samplers,
            this._gpuObjectList.programs,
            this._gpuObjectList.framebuffers,
            this._gpuObjectList.vertexArrayObjects,
            this._gpuObjectList.bindGroups
        ]){
            for (const obj of list){
                this.disposeObject(obj, false);
            }
        }
        if (this.isContextLost()) {
            for (const obj of this._disposeObjectList){
                obj.destroy();
            }
            this._disposeObjectList = [];
        }
    }
    reloadAll() {
        for (const list of [
            this._gpuObjectList.buffers,
            this._gpuObjectList.textures,
            this._gpuObjectList.samplers,
            this._gpuObjectList.programs,
            this._gpuObjectList.framebuffers,
            this._gpuObjectList.vertexArrayObjects,
            this._gpuObjectList.bindGroups
        ]){
            // obj.reload() may change the list, so make a copy first
            for (const obj of list.slice()){
                obj.reload();
            }
        }
        return;
    }
    parseTextureOptions(options) {
        const noMipmapFlag = options?.mipmapping === false ? GPUResourceUsageFlags.TF_NO_MIPMAP : 0;
        const writableFlag = options?.writable ? GPUResourceUsageFlags.TF_WRITABLE : 0;
        const dynamicFlag = options?.dynamic ? GPUResourceUsageFlags.DYNAMIC : 0;
        if (noMipmapFlag && options?.samplerOptions) {
            options.samplerOptions.mipFilter = 'none';
        }
        return noMipmapFlag | writableFlag | dynamicFlag;
    }
    parseBufferOptions(options, defaultUsage) {
        options = options ?? {};
        const usage = options.usage ?? defaultUsage;
        let usageFlag;
        switch(usage){
            case 'uniform':
                usageFlag = GPUResourceUsageFlags.BF_UNIFORM;
                options.managed = false;
                options.dynamic = options.dynamic ?? true;
                break;
            case 'vertex':
                usageFlag = GPUResourceUsageFlags.BF_VERTEX;
                break;
            case 'index':
                usageFlag = GPUResourceUsageFlags.BF_INDEX;
                break;
            case 'read':
                usageFlag = GPUResourceUsageFlags.BF_READ;
                options.managed = false;
                break;
            case 'write':
                usageFlag = GPUResourceUsageFlags.BF_WRITE;
                options.managed = false;
                break;
            case 'pack-pixel':
                usageFlag = GPUResourceUsageFlags.BF_PACK_PIXEL;
                options.managed = false;
                break;
            case 'unpack-pixel':
                usageFlag = GPUResourceUsageFlags.BF_UNPACK_PIXEL;
                options.managed = false;
                break;
            default:
                usageFlag = 0;
                break;
        }
        const storageFlag = options?.storage ?? false ? GPUResourceUsageFlags.BF_STORAGE : 0;
        const dynamicFlag = options?.dynamic ?? false ? GPUResourceUsageFlags.DYNAMIC : 0;
        const managedFlag = dynamicFlag === 0 && (options?.managed ?? true) ? GPUResourceUsageFlags.MANAGED : 0;
        return usageFlag | storageFlag | dynamicFlag | managedFlag;
    }
}

export { BaseDevice };
//# sourceMappingURL=device.js.map
